import React, { useState, useCallback, useEffect } from 'react';
import { X, Plus, Edit2, Shuffle, Trash2, Check, ChevronDown, ChevronRight, Inbox } from 'lucide-react';
import type { ReviewEntry, JournalEntryCreate } from '../types';
import { AdaptiveModal } from './AdaptiveModal';
import { promptsAPI, journalAPI } from '../services/api';
import { REVIEW_DEFAULT_PROMPTS } from '../data/prompts';

type ReviewType = 'weekly' | 'monthly' | 'yearly';

interface ReviewSaveData {
  reflections: ReviewEntry[];
  goals: string[];
  vision?: string;
  values?: string[];
}

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  reviewType: ReviewType;
  periodLabel: string;
  // Period data
  reflections: ReviewEntry[];
  goals: string[];
  vision?: string;
  values?: string[];
  // Previous period context (optional)
  previousPeriod?: {
    goals?: string[];
    reflections?: ReviewEntry[];
    completedTaskCount?: number;
  } | null;
  isLoadingPreviousData?: boolean;
  // Inbox integration
  inboxCount?: number;
  onShowProcessInbox?: () => void;
  // Callbacks
  onSave: (data: ReviewSaveData) => void;
  onComplete: (data: ReviewSaveData) => void;
}

const ACCENT_COLORS: Record<ReviewType, { bg: string; text: string; border: string; button: string; ring: string }> = {
  weekly: {
    bg: 'bg-blue-50',
    text: 'text-blue-600',
    border: 'border-blue-200',
    button: 'bg-blue-600 hover:bg-blue-700',
    ring: 'focus:ring-blue-500',
  },
  monthly: {
    bg: 'bg-purple-50',
    text: 'text-purple-600',
    border: 'border-purple-200',
    button: 'bg-purple-600 hover:bg-purple-700',
    ring: 'focus:ring-purple-500',
  },
  yearly: {
    bg: 'bg-green-50',
    text: 'text-green-600',
    border: 'border-green-200',
    button: 'bg-green-600 hover:bg-green-700',
    ring: 'focus:ring-green-500',
  },
};

const REVIEW_TITLES: Record<ReviewType, string> = {
  weekly: 'Weekly Review',
  monthly: 'Monthly Review',
  yearly: 'Yearly Review',
};

const JOURNAL_SOURCES: Record<ReviewType, 'weekly_review' | 'monthly_review' | 'yearly_review'> = {
  weekly: 'weekly_review',
  monthly: 'monthly_review',
  yearly: 'yearly_review',
};

const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  reviewType,
  periodLabel,
  reflections: initialReflections,
  goals: initialGoals,
  vision: initialVision,
  values: initialValues,
  previousPeriod,
  isLoadingPreviousData,
  inboxCount,
  onShowProcessInbox,
  onSave,
  onComplete,
}) => {
  const colors = ACCENT_COLORS[reviewType];
  const defaults = REVIEW_DEFAULT_PROMPTS[reviewType];

  // State
  const [reflections, setReflections] = useState<ReviewEntry[]>(() => {
    if (Array.isArray(initialReflections) && initialReflections.length > 0) {
      return initialReflections;
    }
    return defaults.map(p => ({ prompt: p, response: '' }));
  });
  const [goals, setGoals] = useState<string[]>(initialGoals || []);
  const [vision, setVision] = useState<string>(initialVision || '');
  const [values, setValues] = useState<string[]>(initialValues || []);
  const [openJournalText, setOpenJournalText] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newValueText, setNewValueText] = useState('');
  const [showPreviousPeriod, setShowPreviousPeriod] = useState(false);

  // Reset state when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setReflections(
        Array.isArray(initialReflections) && initialReflections.length > 0
          ? initialReflections
          : defaults.map(p => ({ prompt: p, response: '' }))
      );
      setGoals(initialGoals || []);
      setVision(initialVision || '');
      setValues(initialValues || []);
      setOpenJournalText('');
      setEditingIndex(null);
      setNewValueText('');
      setShowPreviousPeriod(false);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildSaveData = useCallback((): ReviewSaveData => {
    const data: ReviewSaveData = {
      reflections,
      goals: goals.filter(g => g.trim()),
    };
    if (reviewType === 'yearly') {
      data.vision = vision;
      data.values = values;
    }
    return data;
  }, [reflections, goals, vision, values, reviewType]);

  const handleSaveAndExit = () => {
    onSave(buildSaveData());
    onClose();
  };

  const handleComplete = () => {
    onComplete(buildSaveData());
    onClose();

    // Fire-and-forget journal entries
    const journalEntries: JournalEntryCreate[] = reflections
      .filter(e => e.response.trim())
      .map(e => ({
        content: e.response,
        prompt: e.prompt,
        source: JOURNAL_SOURCES[reviewType],
      }));
    // Add open journal if filled
    if (openJournalText.trim()) {
      journalEntries.push({
        content: openJournalText,
        prompt: 'Open Journal',
        source: JOURNAL_SOURCES[reviewType],
      });
    }
    if (journalEntries.length > 0) {
      journalAPI.createBulk(journalEntries).catch(console.error);
    }
  };

  const handleRandomPrompt = async (index: number) => {
    try {
      const category = reviewType === 'yearly' ? 'weekly' : reviewType;
      const { prompt: randomPrompt } = await promptsAPI.getInspirationRandom(category);
      setReflections(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], prompt: randomPrompt };
        return updated;
      });
    } catch (error) {
      console.error('Failed to get random prompt:', error);
    }
  };

  const addPrompt = async () => {
    try {
      const category = reviewType === 'yearly' ? 'weekly' : reviewType;
      const { prompt } = await promptsAPI.getInspirationRandom(category);
      setReflections(prev => [...prev, { prompt, response: '' }]);
    } catch {
      setReflections(prev => [...prev, { prompt: 'What else is on your mind?', response: '' }]);
    }
  };

  const resetToDefaults = () => {
    setReflections(defaults.map(p => ({ prompt: p, response: '' })));
    setEditingIndex(null);
  };

  const hasPreviousData = previousPeriod && (
    (previousPeriod.goals && previousPeriod.goals.length > 0) ||
    (previousPeriod.reflections && previousPeriod.reflections.some(e => e.response?.trim())) ||
    (previousPeriod.completedTaskCount && previousPeriod.completedTaskCount > 0)
  );

  if (!isOpen) return null;

  return (
    <AdaptiveModal isOpen={isOpen} onClose={onClose} title="" size="xl">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className={`text-2xl font-bold text-gray-900`}>{REVIEW_TITLES[reviewType]}</h2>
            <p className="text-sm text-gray-500 mt-1">{periodLabel}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Inbox processing prompt */}
        {inboxCount != null && inboxCount > 0 && (
          <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Inbox className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  {inboxCount} {inboxCount === 1 ? 'task needs' : 'tasks need'} processing
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Process them before your review for a clear mind
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                if (onShowProcessInbox) {
                  onShowProcessInbox();
                } else {
                  window.dispatchEvent(new CustomEvent('openProcessInbox'));
                }
              }}
              className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 transition-colors flex-shrink-0"
            >
              Process Now
            </button>
          </div>
        )}

        {/* Scrollable content */}
        <div className="max-h-[70vh] overflow-y-auto space-y-8 pr-1">
          {/* Previous Period Context (collapsible) */}
          {(hasPreviousData || isLoadingPreviousData) && (
            <section>
              <button
                onClick={() => setShowPreviousPeriod(!showPreviousPeriod)}
                className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-800 mb-2"
              >
                {showPreviousPeriod ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Previous Period Summary
              </button>
              {showPreviousPeriod && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {isLoadingPreviousData ? (
                    <p className="text-sm text-gray-500">Loading...</p>
                  ) : previousPeriod ? (
                    <>
                      {previousPeriod.goals && previousPeriod.goals.length > 0 && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 mb-1">Goals</h4>
                          <ul className="list-disc list-inside space-y-0.5">
                            {previousPeriod.goals.map((goal, i) => (
                              <li key={i} className="text-sm text-gray-600">{goal}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {previousPeriod.reflections && previousPeriod.reflections.filter(e => e.response?.trim()).length > 0 && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 mb-1">Reflections</h4>
                          {previousPeriod.reflections.filter(e => e.response?.trim()).map((entry, i) => (
                            <div key={i} className="text-sm mb-1">
                              <span className="font-medium text-gray-600">{entry.prompt}:</span>
                              <span className="text-gray-500 ml-1">{entry.response}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {previousPeriod.completedTaskCount != null && previousPeriod.completedTaskCount > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-gray-600">{previousPeriod.completedTaskCount} tasks completed</span>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </section>
          )}

          {/* Vision (yearly only) */}
          {reviewType === 'yearly' && (
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Vision</h3>
              <textarea
                value={vision}
                onChange={(e) => setVision(e.target.value)}
                className={`w-full p-3 border border-gray-300 rounded-lg ${colors.ring} focus:border-transparent`}
                rows={3}
                placeholder="What is your vision for this year?"
              />
            </section>
          )}

          {/* Values (yearly only) */}
          {reviewType === 'yearly' && (
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Core Values</h3>
              <div className="space-y-2">
                {values.map((value, index) => (
                  <div key={index} className={`flex items-center gap-2 p-2 ${colors.bg} rounded-lg ${colors.border} border`}>
                    <span className="flex-1 text-sm font-medium text-gray-800">{value}</span>
                    <button
                      onClick={() => setValues(values.filter((_, i) => i !== index))}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newValueText}
                    onChange={(e) => setNewValueText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newValueText.trim()) {
                        setValues([...values, newValueText.trim()]);
                        setNewValueText('');
                      }
                    }}
                    className="flex-1 p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Add a core value..."
                  />
                  <button
                    onClick={() => {
                      if (newValueText.trim()) {
                        setValues([...values, newValueText.trim()]);
                        setNewValueText('');
                      }
                    }}
                    disabled={!newValueText.trim()}
                    className={`p-2 ${colors.button} text-white rounded-lg disabled:bg-gray-300 transition-colors`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Reflections */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Reflections</h3>
            <div className="space-y-4">
              {reflections.map((entry, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    {editingIndex === index ? (
                      <input
                        type="text"
                        value={entry.prompt}
                        onChange={(e) => {
                          const updated = [...reflections];
                          updated[index] = { ...updated[index], prompt: e.target.value };
                          setReflections(updated);
                        }}
                        onBlur={() => setEditingIndex(null)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setEditingIndex(null); }}
                        className="flex-1 px-2 py-1 text-sm font-medium border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                    ) : (
                      <span className="flex-1 text-sm font-medium text-gray-700">{entry.prompt}</span>
                    )}
                    <div className="flex items-center gap-1 ml-2">
                      <button onClick={() => setEditingIndex(editingIndex === index ? null : index)} className="p-1 text-gray-400 hover:text-gray-600" title="Edit prompt">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleRandomPrompt(index)} className="p-1 text-gray-400 hover:text-gray-600" title="Shuffle prompt">
                        <Shuffle className="w-4 h-4" />
                      </button>
                      <button onClick={() => setReflections(reflections.filter((_, i) => i !== index))} className="p-1 text-gray-400 hover:text-red-600" title="Delete prompt">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <textarea
                    value={entry.response}
                    onChange={(e) => {
                      const updated = [...reflections];
                      updated[index] = { ...updated[index], response: e.target.value };
                      setReflections(updated);
                    }}
                    className={`w-full p-3 border border-gray-300 rounded-lg ${colors.ring} focus:border-transparent`}
                    rows={3}
                    placeholder="Your reflection..."
                  />
                </div>
              ))}

              <div className="flex items-center gap-4">
                <button
                  onClick={addPrompt}
                  className={`flex items-center gap-1 ${colors.text} hover:opacity-80 text-sm`}
                >
                  <Plus className="w-4 h-4" />
                  <span>Add prompt</span>
                </button>
                <button
                  onClick={resetToDefaults}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Reset to defaults
                </button>
              </div>
            </div>
          </section>

          {/* Goals */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Goals</h3>
            <div className="space-y-2">
              {goals.map((goal, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={goal}
                    onChange={(e) => {
                      const newGoals = [...goals];
                      newGoals[index] = e.target.value;
                      setGoals(newGoals);
                    }}
                    className={`flex-1 p-2 border border-gray-300 rounded-lg ${colors.ring} focus:border-transparent`}
                    placeholder={`Goal ${index + 1}`}
                  />
                  <button
                    onClick={() => setGoals(goals.filter((_, i) => i !== index))}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setGoals([...goals, ''])}
                className={`flex items-center gap-1 ${colors.text} hover:opacity-80 text-sm`}
              >
                <Plus className="w-4 h-4" />
                <span>Add Goal</span>
              </button>
            </div>
          </section>

          {/* Open Journal */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Open Journal</h3>
            <textarea
              value={openJournalText}
              onChange={(e) => setOpenJournalText(e.target.value)}
              className={`w-full p-3 border border-gray-300 rounded-lg ${colors.ring} focus:border-transparent`}
              rows={4}
              placeholder="Write freely about anything on your mind..."
            />
          </section>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={handleSaveAndExit}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Save & Exit
          </button>
          <button
            onClick={handleComplete}
            className={`flex items-center gap-2 px-6 py-2 ${colors.button} text-white rounded-lg transition-colors`}
          >
            <Check className="w-4 h-4" />
            <span>Complete Review</span>
          </button>
        </div>
      </div>
    </AdaptiveModal>
  );
};

export default ReviewModal;
